import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Snackbar, Alert, Typography } from '@mui/material';
import { PageRoot, PageContent } from './styles';
import { fetchSafeBrowserInfo } from '../../services/safeBrowserService';
import getOS from '../../utils/getOS';
import Header from './Header';
import RecommendationBanner from './RecommendationBanner';
import DownloadCard from './DownloadCard';
import RequirementsGrid from './RequirementsGrid';
import InstallationInstructions from './InstallationInstructions';
import SupportSection from './SupportSection';
import PageSkeleton from './PageSkeleton';
import ErrorStatePage from './ErrorStatePage';

export default function SafeBrowserDownload() {
    const { test_engine_project: projectShortName } = useParams();
    const [status, setStatus] = useState('loading');
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [downloadingOS, setDownloadingOS] = useState(null);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const detectedOS = getOS();

    useEffect(() => {
        if (!projectShortName) { setStatus('invalid'); return; }

        fetchSafeBrowserInfo(projectShortName)
            .then((response) => {
                if (response.code === 'OK') {
                    const d = response.data;
                    setData(d);
                    const tabs = [];
                    if (d.windows) tabs.push('windows');
                    if (d.mac) tabs.push('mac');
                    const preferred = detectedOS === 'mac' && d.mac ? tabs.indexOf('mac') : 0;
                    setActiveTab(preferred >= 0 ? preferred : 0);
                    setStatus('success');
                } else {
                    resolveNotFound(response.message || '');
                }
            })
            .catch((err) => {
                const resp = err.response?.data;
                if (resp) resolveNotFound(resp.message || '');
                else setStatus('error');
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectShortName]);

    const resolveNotFound = (message) => {
        const m = message.toLowerCase();
        if (m.includes('invalid')) setStatus('invalid');
        else if (m.includes('not configured')) setStatus('not_configured');
        else setStatus('not_found');
    };

    const handleDownload = (platform, url) => {
        if (!url || !url.startsWith('https://')) {
            setToastMessage('Download link is not available. Please try again later.');
            setToastOpen(true);
            return;
        }
        setDownloadingOS(platform);
        try {
            window.location.href = url;
        } catch {
            setToastMessage('Failed to start download. Please try again.');
            setToastOpen(true);
        } finally {
            setTimeout(() => setDownloadingOS(null), 3000);
        }
    };

    if (status === 'loading') return <PageSkeleton />;
    if (status === 'invalid') return <ErrorStatePage title="Invalid Download Link" subtitle="Please contact your administrator." />;
    if (status === 'not_configured' || status === 'not_found') return (
        <ErrorStatePage
            showHeader={false}
            title="404 - Page Not Found"
            subtitle="The download page is currently unavailable."
        />
        // <ErrorStatePage
        //     title="Download Not Available"
        //     subtitle="Please contact your administrator."
        // />
    );
    if (status === 'error') return <ErrorStatePage title="Unable to load download information." subtitle="Please try again later." />;

    const showWinRec = detectedOS === 'windows' && data?.windows;
    const showMacRec = detectedOS === 'mac' && data?.mac;

    return (
        <PageRoot>
            <Header />
            <PageContent>

                <Box textAlign="center" mb={4}>
                    <Typography
                        variant="h4"
                        fontWeight={800}
                        color="#0F172A"
                        sx={{ fontSize: { xs: '1.55rem', md: '1.9rem' }, mb: 1.5, letterSpacing: '-0.02em' }}
                    >
                        Download the Exam Browser
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ maxWidth: 620, mx: 'auto', lineHeight: 1.7 }}
                    >
                        Install this before your exam. It only works with the platform and takes only a few minutes to set up.
                    </Typography>
                </Box>

                {(showWinRec || showMacRec) && (
                    <RecommendationBanner os={showWinRec ? 'windows' : 'mac'} />
                )}

                <Box
                    display="flex"
                    gap={3}
                    mb={3}
                    sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                    component="section"
                    aria-label="Download options"
                >
                    {data?.windows && (
                        <DownloadCard
                            platform="windows"
                            info={data.windows}
                            recommended={showWinRec}
                            downloading={downloadingOS === 'windows'}
                            onDownload={handleDownload}
                        />
                    )}
                    {data?.mac && (
                        <DownloadCard
                            platform="mac"
                            info={data.mac}
                            recommended={showMacRec}
                            downloading={downloadingOS === 'mac'}
                            onDownload={handleDownload}
                        />
                    )}
                </Box>

                <RequirementsGrid />

                <InstallationInstructions
                    data={data}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                <SupportSection helpDeskNumbers={data?.helpDeskNumbers} />

            </PageContent>

            <Snackbar
                open={toastOpen}
                autoHideDuration={5000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setToastOpen(false)}
                    severity="error"
                    variant="filled"
                    sx={{ borderRadius: '10px' }}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        </PageRoot>
    );
}
